import { Component, OnInit, Input, Output, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types';
import { GuestAdjustmentService } from './guest-adjustment.service';
import { AppConfigService } from '../appConfig.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-guest-adjustment',
  templateUrl: './guest-adjustment.component.html',
  styleUrls: ['./guest-adjustment.component.css']
})
export class GuestAdjustmentComponent implements OnInit, OnChanges {

  @Input() guestAdjDetails: any;
  @Output() adjustmentDone: EventEmitter<any> = new EventEmitter<any>();

  public disableSubmit = false;
  public success = false;
  public statusValue = [];
  public creditReasons = [];
  public debitReasons = [];
  public maxCreditLimit = 0;
  public maxDebitLimit = 0;

  public info: any = {
    header: 'Customer Wallet Adjustment',
    headerClass: null,
    dataFetched: true,
    rows: []
  };

  private roles: string[] = [];
  private configLoaded = false;

  constructor(
    private toasterService: ToasterService,
    private guestAdjustmentService: GuestAdjustmentService,
    private appConfigService: AppConfigService,
    public dialog: MatDialog
  ) {}

  async ngOnInit() {
    this.roles = this.accessCookie('Roles').split(',');
    try {
      const config: any = await this.appConfigService.getPoolOrdersAdjustmentConfig();
      const creditLimitMap = config.creditLimit || {};
      const debitLimitMap = config.debitLimit || {};
      this.creditReasons = config.credit_reasons ? JSON.parse(config.credit_reasons) : [];
      this.debitReasons = config.debit_reasons ? JSON.parse(config.debit_reasons) : [];

      this.maxCreditLimit = this.resolveLimit(creditLimitMap);
      this.maxDebitLimit = this.resolveLimit(debitLimitMap);
    } catch (e) {
      console.error('Failed to load hopr adjustment config', e);
    }
    this.configLoaded = true;
    this.buildActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.guestAdjDetails && changes.guestAdjDetails.currentValue) {
      if (!changes.guestAdjDetails.currentValue.dataFetched) {
        this.emptyCard('Cannot load adjustment');
        return;
      }
    }
    if (!this.configLoaded) { return; }
    this.buildActions();
  }

  private resolveLimit(limitMap: Record<string, number>): number {
    const limits = this.roles
      .filter(role => limitMap[role] !== undefined)
      .map(role => limitMap[role]);
    return limits.length > 0 ? Math.max(...limits) : 0;
  }

  private buildActions() {
    if (this.maxCreditLimit > 0) {
      if (!this.statusValue.includes('Credit')) { this.statusValue.push('Credit'); }
    }
    if (this.maxDebitLimit > 0) {
      if (!this.statusValue.includes('Debit')) { this.statusValue.push('Debit'); }
    }
    this.constructData();
  }

  constructData() {
    if (this.statusValue.length > 0) {
      this.info = {
        header: 'Customer Wallet Adjustment',
        headerClass: null,
        dataFetched: true,
        rows: [
          [
            { titleClass: 'form-title-class', title: 'Action', type: 'single-searchable-dropdown',
              values: this.statusValue, placeholder: 'Select action', key: 'hopr-guest-action',
              default: undefined, className: 'col-md-6' },
            { titleClass: 'form-title-class', title: 'Reason', type: 'single-searchable-dropdown',
              values: [], placeholder: 'Select reason', key: 'hopr-guest-reason', className: 'col-md-6' }
          ],
          [
          { titleClass: 'form-title-class', title: 'Amount', type: 'text-input',
            placeholder: 'Enter Amount', key: 'hopr-guest-amount', className: 'col-md-6', numericOnly: true },
          { titleClass: 'form-title-class', title: 'Wallet', type: 'single-searchable-dropdown',
            values: ['Guest Wallet'], placeholder: 'Guest Wallet', key: 'hopr-guest-wallet',
            className: 'col-md-6', default: 'Guest Wallet' }
        ]
      ]
    };
    } else {
      this.info = {
        header: 'Customer Wallet Adjustment',
        accessDenied: true,
        dataFetched: true
      };
    }
  }

  changeRows(event: string) {
    const reasons = event === 'Credit' ? this.creditReasons : this.debitReasons;
    if (event === 'Credit' || event === 'Debit') {
      this.info = {
        header: 'Customer Wallet Adjustment',
        headerClass: null,
        dataFetched: true,
        rows: [
          [
            { titleClass: 'form-title-class', title: 'Action', type: 'single-searchable-dropdown',
              values: this.statusValue, placeholder: 'Select action', key: 'hopr-guest-action',
              default: event, className: 'col-md-6' },
            { titleClass: 'form-title-class', title: 'Reason', type: 'single-searchable-dropdown',
              values: reasons, placeholder: 'Select reason', key: 'hopr-guest-reason', className: 'col-md-6' }
          ],
          [
            { titleClass: 'form-title-class', title: 'Amount', type: 'text-input',
              placeholder: 'Enter Amount', key: 'hopr-guest-amount', className: 'col-md-6', numericOnly: true },
            { titleClass: 'form-title-class', title: 'Wallet', type: 'single-searchable-dropdown',
              values: ['Guest Wallet'], placeholder: 'Guest Wallet', key: 'hopr-guest-wallet',
              className: 'col-md-6', default: 'Guest Wallet' }
          ]
        ]
      };
    }
  }

  async performAdjustment(event: any) {
    const action = event['hopr-guest-action'];
    const reason = event['hopr-guest-reason'];
    const amount = Number(event['hopr-guest-amount']);

    if (!action) {
      this.showWarning('Please select action'); return;
    }
    if (!reason) {
      this.showWarning('Please select reason'); return;
    }
    if (!amount || amount <= 0) {
      this.showWarning('Please enter a valid amount'); return;
    }
    if (!(parseFloat(event['hopr-guest-amount']) == event['hopr-guest-amount'])) {
      this.showWarning('Please enter a valid number'); return;
    }

    const maxLimit = action === 'Credit' ? this.maxCreditLimit : this.maxDebitLimit;
    if (amount > maxLimit) {
      this.showWarning(`You are allowed to ${action} max Rs. ${maxLimit}`); return;
    }

    const userDetails = JSON.parse(this.accessCookie('user'));
    const profileId = this.guestAdjDetails.profileId;
    const orderId = this.guestAdjDetails.orderId;
    const paymentRefId = this.generateUuid();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '335px',
      data: {
        title: `Are you sure you want to ${action}?`,
        type: 'order-details',
        reason: action,
        message: '',
        buttonText: `Yes, ${action}`
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.disableSubmit = true;

      const roles = this.accessCookie('Roles').split(',');
      const sharedBody = {
        profileId,
        channel: 'dashboard_order_adjustment',
        amount,
        title: reason,
        paymentRefId,
        orderId,
        actorId: userDetails['_id'],
        actorEmail: userDetails['emailId'],
        actorRoles: roles,
        ownerId: profileId,
        ownerType: 'guest',
        source: 'profiles-dashboard',
        remarks: reason
      };

      if (action === 'Credit') {
        this.guestAdjustmentService.creditGuestWallet(sharedBody).subscribe(
          () => {
            this.showSuccess('Amount credited successfully');
            this.adjustmentDone.emit();
            this.disableSubmit = false;
          },
          err => {
            this.disableSubmit = false;
            this.showWarning(err && err.error && err.error.message || 'Unable to credit amount');
          }
        );
      } else {
        this.guestAdjustmentService.debitGuestWallet(sharedBody).subscribe(
          () => {
            this.showSuccess('Amount debited successfully');
            this.adjustmentDone.emit();
            this.disableSubmit = false;
          },
          err => {
            this.disableSubmit = false;
            this.showWarning(err && err.error && err.error.message || 'Unable to debit amount');
          }
        );
      }
    });
  }

  private showWarning(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message }));
  }

  private showSuccess(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.SUCCESS, message }));
    this.success = true;
  }

  private emptyCard(message?: string) {
    this.info = {
      header: 'Customer Wallet Adjustment',
      dataFetched: false,
      emptyMessage: message || 'No data available'
    };
  }

  private generateUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private accessCookie(cookieName: string): string {
    const name = cookieName + '=';
    const allCookieArray = document.cookie.split(';');
    for (let i = 0; i < allCookieArray.length; i++) {
      if (allCookieArray[i]) {
        const cookieItem = allCookieArray[i].trim();
        if (cookieItem.indexOf(name) === 0) {
          return cookieItem.substring(name.length, cookieItem.length);
        }
      }
    }
    return '';
  }
}
